import { mutationTypes } from '@/types';
import {
	createGlossaryExhibition,
	createCalendarEventsExhibition,
	createRootExhibition,
} from '@/tools/virtualExhibitionCreator';
import { logger } from '@/tools/logger';
import { createObjectFromArrayByKey } from '@/tools/createObjectFromArray';
import { mutuallyExclusiveComponents, uiStates, mutuallyExclusiveComponentsList } from '@/tools/viewState';

function isEmptyObject(obj) {
	return typeof obj !== 'object' || (Object.entries(obj).length === 0 && obj.constructor === Object);
}

export default {
	[mutationTypes.storeModuleLoaded](state, { moduleName }) {
		state.loadedStoreModules[moduleName] = true;
	},
	[mutationTypes.setIsApp](state) {
		state.isApp = true;
	},
	[mutationTypes.setVirtualPackageSyncer](state) {
		state.isVirtualPackageSyncer = true;
	},
	[mutationTypes.setIsPreviewDataSource](state) {
		state.isPreviewDataSource = true;
	},
	// Called if is WC is started on Loaner Device
	[mutationTypes.isLoaner](state) {
		state.isLoaner = true;
	},
	[mutationTypes.setViewportSize](state, { size }) {
		state.viewportSize = size;
	},
	[mutationTypes.setViewportMode](state, { mode }) {
		state.viewportMode = mode;
	},
	[mutationTypes.setFontSize](state, { size }) {
		state.fontSize = size;
	},
	[mutationTypes.setColorMode](state, { colorMode }) {
		state.colorMode = colorMode;
	},
	[mutationTypes.setImageSize](state, { size }) {
		state.imageSize = size;
	},
	[mutationTypes.sendMessage](state, message) {
		state.messages.push(Object.assign(message, { id: Date.now() * 10 + state.messages.length }));
	},
	[mutationTypes.removeMessage](state, { messageID }) {
		const toBeDeleted = state.messages.find(({ id }) => id === messageID);
		const index = state.messages.indexOf(toBeDeleted);
		if (index > -1) {
			state.messages.splice(index, 1);
		}
	},
	[mutationTypes.removeAllMessages](state) {
		state.messages = [];
	},
	// The input parameter `article` is of mixed type to either an id or a complete
	// article can be passed, if a complete article is passed the visit history of
	// locations can also be build up.
	[mutationTypes.setArticleID](state, { article }) {
		let articleID = article;
		if (typeof article !== 'string') {
			articleID = article.id;
			state.visitedLocationIDs = (article.triggers || []).reduce(
				(acc, { locationID }) => Object.assign(acc, { [locationID]: 1 }),
				state.visitedLocationIDs
			);
		}
		state.currentArticleID = articleID;
		state.visitedArticleIDs[articleID] = 1;
	},
	[mutationTypes.setExhibitionID](state, { exhibition }) {
		let exhibitionID = exhibition;
		if (typeof exhibition !== 'string') {
			exhibitionID = exhibition.exhibitionID || exhibition.id;
		}
		if (!isEmptyObject(exhibition.navigationJson)) {
			state.navigation = Object.assign(state.navigation, exhibition.navigationJson);
		} else if (!isEmptyObject(state.dataSourceCFG.global.navigationJson)) {
			state.navigation = Object.assign(state.navigation, state.dataSourceCFG.global.navigationJson);
		}
		state.defaultLanguage = exhibition.defaultLanguage;
		logger.log(`set left navigation menu to ${state.navigation.left.map(({ name }) => name)}`);
		state.currentExhibitionID = exhibitionID;
		state.visitedExhibtionIDs[exhibitionID] = 1;
	},
	/**
	 * This mutations adds Content to the ContentMap as key the conten id is used
	 * the content is added as objects with language as key
	 * id is not only articleID also glossaryTermID and calendarEventID
	 * @param {*} state
	 * @param {id, language, content} values
	 */
	[mutationTypes.setContentForArticle](state, { id, language, content }) {
		state.articleContent[id] = Object.assign({ [language]: content }, state.articleContent[id]);
		state.articleContent = Object.assign({}, state.articleContent);
	},
	/**
	 *
	 * @param {*} state
	 * @param {isLoading, modal} params isLoading Boolean if is loading or nor, modal indicates if modal is Loading
	 */
	[mutationTypes.setLanguage](state, { language }) {
		state.persistent.language = language;
	},
	[mutationTypes.addToMediaObjectMap](state, { mediaObjectArray }) {
		mediaObjectArray.forEach(([id, meataData]) => {
			state.currentMediaObjectMap.set(id, meataData);
		});
	},
	[mutationTypes.setDatasource](state, { dataSourceURL, dataSourceCFG }) {
		state.dataSourceURL = dataSourceURL;
		state.dataSourceCFG = dataSourceCFG;
	},
	[mutationTypes.setStyleUrl](state, { styleTemplateURL }) {
		logger.log('Mutation StyleTemplateURL ', styleTemplateURL.toString());
		state.styleTemplateURL = styleTemplateURL.toString();
		if (state.styleTemplateURL !== styleTemplateURL) state.styleLoaded = false;
	},
	[mutationTypes.activeCssMode](state) {
		state.cssDevMode = true;
	},
	[mutationTypes.setStyleLoaded](state, { status }) {
		state.styleLoaded = status;
	},
	/**
	 * This Commit sets NavigationButtons
	 * @param {*} state
	 * @param { navigation } options the navigationObject will be merged into state.navigation
	 */
	[mutationTypes.setNavigation](state, { navigation }) {
		if (navigation.left) logger.warn('This code is deprecated, please migrate the navigation to the CMS');
		state.navigation = Object.assign(state.navigation, navigation);
	},
	[mutationTypes.setBrowserbarColor](state, { browserBarColor }) {
		state.browserBarColor = browserBarColor;
	},
	[mutationTypes.setGlossary](state, { glossary }) {
		state.glossaryByKeyword = Object.entries(
			glossary.reduce((acc, item) => {
				[item.term, ...item.keywords].forEach((word) => {
					Object.assign(acc, { [word]: { definition: item.definition, id: item.id } });
				});
				return acc;
			}, {})
		).map((item) => [new RegExp(`(${item[0]})(\\s+)`, 'gi'), item[1]]);
		state.glossary = glossary;
		state.glossaryMap = createObjectFromArrayByKey(glossary, 'id');
		// Add to exhibitions to enable normal content loading flow
		const { landingExhibition } = state.dataSourceCFG;
		const virtualGlossaryExhibition = createGlossaryExhibition({ landingExhibition, glossary });
		state.dataSourceCFG.exhibitions = state.dataSourceCFG.exhibitions.filter(
			(ex) => ex.exhibitionID !== virtualGlossaryExhibition.exhibitionID
		);
		state.dataSourceCFG.exhibitions.push(virtualGlossaryExhibition);
		state.dataSourceCFG.exhibitionMap[virtualGlossaryExhibition.exhibitionID] = virtualGlossaryExhibition;
	},
	[mutationTypes.setCalendarEvents](state, { calendarEvents }) {
		state.calendarEvents = calendarEvents;
		const { landingExhibition } = state.dataSourceCFG;
		const virtualCalendarEventExhibition = createCalendarEventsExhibition({
			landingExhibition,
			calendarEvents: calendarEvents,
		});
		state.dataSourceCFG.exhibitions = state.dataSourceCFG.exhibitions.filter(
			(ex) => ex.exhibitionID !== virtualCalendarEventExhibition.exhibitionID
		);
		state.dataSourceCFG.exhibitions.push(virtualCalendarEventExhibition);
		state.dataSourceCFG.exhibitionMap[virtualCalendarEventExhibition.exhibitionID] =
			virtualCalendarEventExhibition;
		state.calendarEventMap = createObjectFromArrayByKey(calendarEvents, 'id');
	},
	[mutationTypes.toggleInViewState](state, { id }) {
		if (typeof state.viewState[id] === 'undefined') {
			state.viewState[id] = {
				name: id,
				uiState: uiStates.hidden,
			};
		}
		if (state.viewState[id].uiState !== uiStates.hidden) {
			state.viewState[id].uiState = uiStates.hidden;
		} else {
			if (mutuallyExclusiveComponents.has(id)) {
				// Remove all other components from view state before adding.
				mutuallyExclusiveComponentsList.forEach(
					(uiComponent) => (state.viewState[uiComponent].uiState = uiStates.hidden)
				);
			}
			state.viewState[id].uiState = uiStates.maximised;
		}
		state.viewState = Object.assign({}, state.viewState);
	},
	[mutationTypes.viewStateSetUiState](state, newValue) {
		const uiStateList = Array.isArray(newValue) ? newValue : [newValue];
		uiStateList.forEach(({ id, uiState }) => {
			if (typeof state.viewState[id] === 'undefined') {
				// Adds uiCompontent to view state i
				state.viewState[id] = {
					name: id,
					uiState: uiStates.hidden,
				};
			}
			if (!Object.values(uiStates).includes(uiState)) {
				logger.error(`viewStateUnknownUiuiState: ${uiState}`);
			}
			state.viewState[id].uiState = uiState;
		});
		state.viewState = Object.assign({}, state.viewState);
	},
	[mutationTypes.augmentedRealitySetArticle](state, { article, previousPath }) {
		state.currentAugmentedRealityArticle = article;
		if (previousPath) state.augmentedRealityPreviousPath = previousPath;
	},
	[mutationTypes.patchState](state, patch) {
		Object.assign(state, patch);
	},
	[mutationTypes.patchStateByPath](state, { path, value }) {
		const pathParts = path.split('.');
		const lastPart = pathParts.pop();
		const currentRoot = pathParts.reduce((root, currentPath) => root[currentPath], state);
		currentRoot[lastPart] = value;
	},
	/**
	 * This mutations set the value of field in the store by its storeURI
	 * @param {*} state
	 * @param {*} article
	 */
	[mutationTypes.setStoreValueByURI](state, { storeURI, value }) {
		const storePath = storeURI.slice(8).split('.');
		if (storePath[0] !== 'state') return logger.error('Only state can be set');
		let result = state[storePath[1]];
		if (typeof result === 'undefined') {
			logger.error(`Could not find Store Value with path ${storePath[0]} in ${storePath}`);
			return undefined;
		}
		for (let i = 2; i < storePath.length - 1; i++) {
			if (typeof result[storePath[i]] === 'undefined') {
				logger.error(`Could not find Store Value with path ${storePath[i]} in ${storePath} `);
				return undefined;
			}
			if (storePath[i] === 'languageContent') {
				// here language Content is reduce to an ObjectMap so it behaves like language butto content values

				result = result[storePath[i]].find(
					(languageContent) => languageContent.languageISO === state.persistent.language
				);
			} else {
				result = result[storePath[i]];
			}
		}
		result[storePath[storePath.length - 1]] = value;
	},
	[mutationTypes.nativeInterfaceUp](state) {
		state.hasNativeInterface = true;
	},
	[mutationTypes.setArticleTransition](state, transitionName) {
		if (!state.isSwipeNavigation) {
			state.articleTransition = transitionName;
		}
	},

	[mutationTypes.setRootExhibition](state) {
		const { landingExhibition } = state.dataSourceCFG;
		const virtualRootExhibition = createRootExhibition({
			landingExhibition,
		});
		state.dataSourceCFG.exhibitions = state.dataSourceCFG.exhibitions.filter(
			(ex) => ex.exhibitionID !== virtualRootExhibition.exhibitionID
		);
		state.dataSourceCFG.exhibitions.push(virtualRootExhibition);
		state.dataSourceCFG.exhibitionMap[virtualRootExhibition.exhibitionID] = virtualRootExhibition;
	},
	[mutationTypes.setUrlValidated](state, validated) {
		state.urlIsValidated = validated;
	},
	[mutationTypes.setLandingExhibit](state, { articleID, exhibitionID }) {
		state.dataSourceCFG.global.landingExhibit = { articleID, exhibitionID };
	},
	[mutationTypes.setMediaplayer](state, { mode, mac }) {
		state.mediaplayer = { mode, mac };
	},
	[mutationTypes.itIsNoScreenReader](state) {
		state.itIsNoScreenReader = true;
	},
	[mutationTypes.setRouteChangedByBrowser](state, { routeChangedByBrowser }) {
		state.routeChangedByBrowser = routeChangedByBrowser;
	},
	[mutationTypes.setRouteChangedByClick](state, { routeUserClicked }) {
		state.routeUserClicked = routeUserClicked;
	},
	[mutationTypes.setPageTransitionMode](state, { transition }) {
		state.pageTransitionMode = transition;
	},
};
